import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import {
    AnnualReportBusinessModel,
    AnnualReportProvider,
    IOptions,
    SortingItemsHelper,
    onGetAnnualReportsByCompanyIdSuccessfully,
} from '@modeso/aumico-lib-annualreport-fe';
import { CompaniesProvider, onCompanyValuationSuccess } from '@modeso/aumico-lib-companies-fe';
import {
    AumBreadcrumbComponentItem,
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
    IPageHeader,
    MAT_DIALOG_CUSTOM_CLASS,
} from '@modeso/aumico-lib-core-fe';
import { AnnualReportTemplate, IAnnualReport, PrintLocation } from '@modeso/types__aumico-lib-annualreport-be';
import { ICompanyNameIdLegalFormDTO } from '@modeso/types__aumico-lib-companies-be';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { I18NextService } from 'angular-i18next';
import { filter, first } from 'rxjs';
import { ExportAnnualReportExcel } from '../../../../../../aumico-lib-annualreport-fe/src/lib/interfaces/export-excel/annual-report-excel.interface';
import { CompanyValuationRequestDTO } from '../../../../../../aumico-lib-companies-fe/src/lib/dtos/company-valuation-request-dto.interface';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { environment } from '../../../../environments/environment';
import { ValuationOrderConfirmationDialogComponent } from '../../../components/valuation-order-confirmation-dialog/valuation-order-confirmation-dialog.component';
import { ValuationReportDialogComponent } from '../../../components/valuation-report-dialog/valuation-report-dialog.component';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { IPdfTronMapper } from '../../../interfaces/pdf-tron-mapper.interface';
import { BasePageComponent } from '../../../pages/base.page';
import { localizationKeys } from '../../../utils/localizationKeys';
import { ExcelMapper } from '../../ms-annual-reports/mappers/excel.mapper';
import { PdfTronGermanMapper } from '../../ms-annual-reports/mappers/pdf-tron-german-mapper';
import { PdfTronSwissMapper } from '../../ms-annual-reports/mappers/pdf-tron-swiss-mapper';

const predifinedLablesKeys = [
    'aumico_reports_annualreport_cover_label_title_1st_prefix',
    'aumico_reports_annualreport_cover_label_title_2nd_prefix',
    'aumico_reports_annualreport_page_print_account_mapping_label_total',
    'aumico_reports_annualreport_page_print_account_mapping_label_word_before_currency',
    'aumico_annual_report_print_preview_balance',
    'aumico_annual_report_print_preview_income',
    'aumico_annual_report_print_preview_attachement',
    'aumico_annual_report_print_preview_aop',
    'aumico_annual_report_print_preview_cashflow',
    'aumico_annual_report_print_preview_ems',
    'aumico_annual_report_print_preview_fas',
    'aumico_annual_report_proposal_carried_forward_to_new_account',
    'aumico_annual_report_proposal_available_retained_earnings',
    'aumico_annual_report_profit_calculation_header',
    'aumico_reports_annualreport_page_print_account_mapping_label_notes',
    'aumico_annual_report_cash_flow_total_recapitulation',
    'aumico_annual_report_cash_flow_total_cashAndCashStart',
    'aumico_annual_report_cash_flow_total_cashAndCashEnd',
    'aumico_annual_report_ems_row_title',
    'aumico_annual_report_ems_business_year',
    'aumico_annual_report_ems_total_equity_header_text',
    'aumico_reports_annualreport_toc_label_table_of_contents',
    'aumico_annual_report_profit_calculation_annual_profit',
    'aumico_report_fas_print_section_costs_title',
    'aumico_report_fas_print_costs_additions',
    'aumico_report_fas_print_costs_disposals',
    'aumico_report_fas_print_costs_transfers',
    'aumico_report_fas_print_section_costs_title',
    'aumico_report_fas_print_section_depr_title',
    'aumico_report_fas_print_depreciations_additions',
    'aumico_report_fas_print_depreciations_disposals',
    'aumico_annual_report_print_preview_fcp',
    'aumico_report_fas_print_depreciations_transfers',
    'aumico_report_fas_print_section_valueApr_title',
    'aumico_report_fas_print_section_bookvalue_current',
    'aumico_report_fas_print_section_bookvalue_previous',
    'aumico_report_fas_print_subtotal',
    'aumico_annual_report_print_preview_account_proof_for_income',
    'aumico_annual_report_print_preview_account_proof_for_balance',
    'aumico_annual_report_account_proof_display_account_number_column_header',
    'aumico_annual_report_account_proof_display_account_name_column_header',
    'aumico_annual_report_display_current_date_column_header',
    'aumico_annual_report_display_previous_date_column_header',
    'aumico_annual_report_fas_direct_assigned_accounts',
    'aumico_reports_annualreport_print_label_previous_year',
    'aumico_reports_annualreport_print_label_current_year',
    'aumico_reports_annualreport_print_label_ratio',
    'aumico_reports_annualreport_print_label_taxdomicile',
    'aumico_reports_annualreport_print_label_taxid',
    'aumico_reports_annualreport_print_label_assets',
    'aumico_reports_annualreport_print_label_liabilities',
    'aumico_reports_annualreport_print_label_page',
    'aumico_reports_print_title_establishment',
    'aumico_reports_annualreport_print_date_from',
    'aumico_reports_annualreport_print_date_to',
    'aumico_reports_annualreport_print_date_of',
];

@UntilDestroy()
@Component({
    selector: 'app-page-valuation-report.page',
    templateUrl: './valuation-report.page.html',
    styleUrls: ['./valuation-report.page.scss'],
})
export class ValuationReportPage extends BasePageComponent implements OnInit {
    localizationKeys = localizationKeys;
    readonly currentLanguage = this.getCurrentLanguageWithFallBack();
    readonly defaultLanguage = environment.defaultLanguage;
    private companyValuationRequest: CompanyValuationRequestDTO[] = [];
    private pdfTronMapper: IPdfTronMapper | undefined;

    constructor(
        private readonly injector: Injector,
        private readonly companiesProvider: CompaniesProvider,
        private readonly annualReportProvider: AnnualReportProvider,
        private readonly matDialog: MatDialog,
        private matIconRegistry: MatIconRegistry,
        private readonly i18NextService: I18NextService,
        private readonly excelMapper: ExcelMapper,
        private readonly mixpanelService: MixpanelService,
    ) {
        super(injector);
        this.matIconRegistry.addSvgIcon(
            'aumico-pattern-icon-lock',
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/aumico-pattern-icon-lock.svg`),
        );
    }

    readonly breadcrumbItems: AumBreadcrumbComponentItem[] = [
        {
            displayName: RouteLabels.COMPANY_VALUATION,
        },
    ];
    readonly pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.VALUATION_REPORT,
    };

    override ngOnInit() {
        super.ngOnInit();
        this.companiesProvider.dispatchCompaniesNameIdLegalFormList();
        this.actionsListener$
            .pipe(ofType(onGetAnnualReportsByCompanyIdSuccessfully), untilDestroyed(this))
            .subscribe((payload) => {
                const annualReportsExcels = this.getAnnualReportsExcels(payload.payload);
                this.companyValuationRequest[FIRST_ELEMENT_INDEX].exportAnnualReportsExcels = annualReportsExcels;
                this.companiesProvider.dispatchCompanyValuation(this.companyValuationRequest);
            });
        this.actionsListener$.pipe(ofType(onCompanyValuationSuccess), untilDestroyed(this)).subscribe(() => {
            this.openCompanyValuationOrderConfirmationPopUp();
        });
    }

    onOrderValuationReportClick(): void {
        this.mixpanelService.track(MIXPANEL_EVENTS.COMPANY_VALUATION_REPORT_ORDER_BUTTON_CLICK);

        let dialogRef: MatDialogRef<ValuationReportDialogComponent, INameId>;
        this.companiesProvider
            .getCompaniesNameIdLegalForm$()
            .pipe(
                filter((companies) => companies != undefined),
                first(),
                untilDestroyed(this),
            )
            .subscribe((companies) => {
                dialogRef = this.matDialog.open<ValuationReportDialogComponent, ICompanyNameIdLegalFormDTO[], INameId>(
                    ValuationReportDialogComponent,
                    {
                        width: '600px',
                        panelClass: MAT_DIALOG_CUSTOM_CLASS,
                        data: companies,
                    },
                );
            });

        dialogRef
            .afterClosed()
            .pipe(
                filter((selectedCompany) => selectedCompany != null),
                untilDestroyed(this),
            )
            .subscribe((selectedCompany) => {
                this.companyValuationRequest[FIRST_ELEMENT_INDEX] = {
                    companyName: selectedCompany.name,
                    companyId: selectedCompany.id,
                    exportAnnualReportsExcels: [],
                };
                this.annualReportProvider.dispatchGetAnnualReportsByCompanyId(selectedCompany.id);
            });
    }

    private getAnnualReportsExcels(annualReports: IAnnualReport[]): ExportAnnualReportExcel[] {
        const annualReportExcels: ExportAnnualReportExcel[] = [];
        for (const annualReport of annualReports) {
            if (
                annualReport.generalInformation == null ||
                annualReport.finStatements.length === EMPTY_ARRAY_LENGTH ||
                annualReport.printOptions == null
            ) {
                continue;
            }
            this.intializePdfTronMapper(annualReport.annualReportTemplate);

            const options: IOptions = {
                showAdjustmentInSums: false,
                showUID: false,
                showRatios: false,
                showCover: false,
                showTOC: false,
                showNotesInDetailInToc: false,
                showShareholderInfo: false,
                showCompanyInfos: false,
                disableBalanceSheetPageBreak: false,
                displayAssetsAndLiabilitiesInSamePage: false,
                textColor: '',
                titleColor: '',
                highlightColor: '',
                backgroundColorPY: '',
                tableHeaderColor: '',
                tableFooterColor: '',
                showReportCompanyLogo: false,
                internalLabel: "Intern",
                showRoundingDisclaimer: false,
                showAccounts: annualReport.printOptions.displayOptions.showAccounts,
                repeatAppendixTitle: annualReport.printOptions.displayOptions.repeatAppendixTitle,
                isSealed: annualReport.isSealed ?? false,
                showPageNumbers: true,
            };
            const predefinedLabels: Map<string, string> = new Map<string, string>();
            for (const predifinedLableKey of predifinedLablesKeys) {
                predefinedLabels.set(
                    predifinedLableKey,
                    this.i18NextService.t(predifinedLableKey, { lng: annualReport.locale }) as string,
                );
            }

            const complementaryReportsAsAppendix = annualReport.complementaryReports.filter(
                (report) => report.printLocation === PrintLocation.APPENDIX,
            );

            const annualReportWithSortedItems = SortingItemsHelper.prepareSortedItemsInAnnualReport(annualReport);

            // we are deciding here which fin statement to use instead of using the ngrx data
            // we are deciding with the same logic as there. If fcp was used, we use the fcp output
            // otherwise the normal fin statements
            const pdfTronSections = this.pdfTronMapper.getSections(
                annualReportWithSortedItems,
                annualReport.printOptions,
                annualReportWithSortedItems,
                annualReport.printOptions.reportOrder,
                options,
                predefinedLabels,
                this.getCurrentLanguageWithFallBack(),
                annualReport.locale,
                annualReport.locale,
                complementaryReportsAsAppendix,
                annualReport.fcp ? annualReport.fcp.outputFinStatements : annualReport.finStatements,
            );

            const excelData = this.excelMapper.mapPdfTronSectionsDataToExcelData(pdfTronSections);
            annualReportExcels.push({
                sheets: excelData,
            });
        }

        return annualReportExcels;
    }

    private intializePdfTronMapper(annualReportTemplate: AnnualReportTemplate | null): void {
        if (annualReportTemplate == null || annualReportTemplate.businessModel === AnnualReportBusinessModel.OR) {
            this.pdfTronMapper = this.injector.get(PdfTronSwissMapper);
            return;
        }

        this.pdfTronMapper = this.injector.get(PdfTronGermanMapper);
    }

    private openCompanyValuationOrderConfirmationPopUp(): void {
        this.matDialog.open<ValuationOrderConfirmationDialogComponent>(ValuationOrderConfirmationDialogComponent, {
            width: '500px',
            panelClass: MAT_DIALOG_CUSTOM_CLASS,
        });
    }

    onDownloadExampleValuationFileClick(): void {
        this.mixpanelService.track(MIXPANEL_EVENTS.COMPANY_VALUATION_EXAMPLE_REPORT_DOWNLOADED);
        this.companiesProvider.downloadDefaultCompanyValuationFile();
    }
}
